"use client"

import { useState, useEffect } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Eye, X, FileText, ImageIcon, File, Clock, AlertTriangle, Megaphone, Plus, Calendar } from "lucide-react"
import { toast, Toaster } from "react-hot-toast"
import { API_ENDPOINTS } from '../../../constants/config';
import Pagination from "../../Common/Pagination";

function AnnouncementsContent() {
  const [announcements, setAnnouncements] = useState([])
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [total, setTotal] = useState(0)
  const [newAnnouncement, setNewAnnouncement] = useState({
    title: "",
    content: "",
    role: "all",
  })
  const [files, setFiles] = useState([])
  const [previews, setPreviews] = useState([])
  const [error, setError] = useState("")
  const [previewModalOpen, setPreviewModalOpen] = useState(false)
  const [selectedImage, setSelectedImage] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [loading, setLoading] = useState(true)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null)
  const [detailsModalOpen, setDetailsModalOpen] = useState(false)

  useEffect(() => {
    fetchAnnouncements()
  }, [page, limit])

  const fetchAnnouncements = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem("token")
      const url = new URL(API_ENDPOINTS.announcementsAdmin)
      url.searchParams.set("paginate", "true")
      url.searchParams.set("page", String(page))
      url.searchParams.set("limit", String(limit))
      const response = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (response.status === 403) {
        setAnnouncements([])
        setError("")
        return
      }
      const data = await response.json()
      setAnnouncements(data.data?.announcements ?? [])
      setTotal(data.data?.pagination?.total ?? 0)
      setError("")
    } catch (error) {
      if (error.response?.status === 403) {
        setAnnouncements([])
        setError("")
        return
      }
      setError("Failed to fetch announcements: " + (error.response?.data?.message || error.message))
      toast.error("Failed to fetch announcements")
    } finally {
      setLoading(false)
    }
  }

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files)
    setFiles((prev) => [...prev, ...selectedFiles])

    // Generate previews
    selectedFiles.forEach((file) => {
      if (file.type.startsWith("image/")) {
        const reader = new FileReader()
        reader.onload = (event) => {
          setPreviews((prev) => [
            ...prev,
            {
              id: Math.random().toString(36).substring(2),
              type: "image",
              url: event.target.result,
              file: file,
              name: file.name,
            },
          ])
        }
        reader.readAsDataURL(file)
      } else {
        // For non-image files
        const fileType = file.type.includes("pdf") ? "pdf" : file.type.includes("doc") ? "doc" : "file"
        setPreviews((prev) => [
          ...prev,
          {
            id: Math.random().toString(36).substring(2),
            type: fileType,
            file: file,
            name: file.name,
          },
        ])
      }
    })
  }

  const removePreview = (id) => {
    const preview = previews.find((p) => p.id === id)
    setPreviews(previews.filter((p) => p.id !== id))
    setFiles(files.filter((f) => !previews.find((p) => p.id === id)?.file || f !== preview?.file))
  }

  const viewAttachment = (attachment) => {
    if (attachment.fileType === "image") {
      setSelectedImage(attachment.url)
      setPreviewModalOpen(true)
    } else {
      window.open(attachment.url, "_blank")
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      const token = localStorage.getItem("token")
      const formData = new FormData()

      formData.append("title", newAnnouncement.title)
      formData.append("content", newAnnouncement.content)
      formData.append("role", newAnnouncement.role)

      files.forEach((file) => {
        formData.append("attachments", file)
      })

      await fetch(API_ENDPOINTS.announcements, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      })

      fetchAnnouncements()
      setNewAnnouncement({ title: "", content: "", role: "all" })
      setFiles([])
      setPreviews([])
      setError("")
      setCreateDialogOpen(false)
      toast.success("Announcement created successfully!")
    } catch (error) {
      setError("Failed to create announcement: " + (error.response?.data?.message || error.message))
      toast.error("Failed to create announcement")
    } finally {
      setIsSubmitting(false)
    }
  }

  const openDetailsModal = (announcement) => {
    setSelectedAnnouncement(announcement)
    setDetailsModalOpen(true)
  }

  const renderFileIcon = (fileType) => {
    switch (fileType) {
      case "image":
        return <ImageIcon className="h-6 w-6 text-blue-500" />
      case "pdf":
        return <FileText className="h-6 w-6 text-red-500" />
      default:
        return <File className="h-6 w-6 text-gray-500" />
    }
  }

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' }
    return new Date(dateString).toLocaleDateString(undefined, options)
  }

  const resetForm = () => {
    setNewAnnouncement({ title: "", content: "", role: "all" })
    setFiles([])
    setPreviews([])
  }

  return (
    <div className="space-y-6">
      {/* Toast notifications */}
      <Toaster position="top-right" toastOptions={{ duration: 3000 }} />

      <Card className="border-0 shadow-lg rounded-xl overflow-hidden">
        <CardHeader className="bg-[#0263cc]/5 border-b border-[#0263cc]/10 pb-4 flex flex-row items-center justify-between">
          <CardTitle className="flex items-center">
            <Megaphone className="h-6 w-6 mr-2 text-[#0263cc]" />
            <span className="text-[#0263cc] text-xl font-semibold">Announcements Management</span>
          </CardTitle>
          
          <Dialog open={createDialogOpen} onOpenChange={(open) => {
            setCreateDialogOpen(open)
            if (!open) resetForm()
          }}>
            <DialogTrigger asChild>
              <Button className="bg-[#0263cc] hover:bg-[#0263cc]/90 text-white">
                <Plus className="h-4 w-4 mr-2" /> New Announcement
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-xl font-semibold flex items-center text-[#0263cc]">
                  <Plus className="h-5 w-5 mr-2" />
                  Create New Announcement
                </DialogTitle>
              </DialogHeader>
              
              {/* Create Announcement Form */}
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                    Title
                  </label>
                  <Input
                    id="title"
                    value={newAnnouncement.title}
                    onChange={(e) => setNewAnnouncement({ ...newAnnouncement, title: e.target.value })}
                    className="border-[#0263cc]/20 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc]"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-1">
                    Content
                  </label>
                  <Textarea
                    id="content"
                    value={newAnnouncement.content}
                    onChange={(e) => setNewAnnouncement({ ...newAnnouncement, content: e.target.value })}
                    rows={4}
                    className="border-[#0263cc]/20 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc]"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
                    Audience
                  </label>
                  <Select
                    id="role"
                    value={newAnnouncement.role}
                    onValueChange={(value) => setNewAnnouncement({ ...newAnnouncement, role: value })}
                  >
                    <SelectTrigger className="border-[#0263cc]/20 focus:border-[#0263cc] focus:ring-1 focus:ring-[#0263cc]">
                      <SelectValue placeholder="Select audience" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="teacher">Teachers Only</SelectItem>
                      <SelectItem value="student">Students Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* File Upload Section */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Attachments</label>
                  <div className="flex items-center space-x-2">
                    <Button
                      type="button"
                      variant="outline"
                      className="cursor-pointer bg-gray-100 hover:bg-gray-200 transition flex items-center gap-2"
                      onClick={() => document.getElementById("file-upload").click()}
                    >
                      <input
                        id="file-upload"
                        type="file"
                        multiple
                        onChange={handleFileChange}
                        className="hidden"
                        accept="image/*,.pdf,.doc,.docx"
                      />
                      <span className="text-sm">Add Files</span>
                    </Button>
                    <span className="text-xs text-gray-500">Images, PDFs, and documents</span>
                  </div>
                </div>

                {/* File Previews */}
                {previews.length > 0 && (
                  <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-4">
                    {previews.map((preview) => (
                      <div key={preview.id} className="relative group border rounded p-2">
                        <Button
                          type="button"
                          variant="destructive"
                          size="icon"
                          className="absolute -top-2 -right-2 h-6 w-6 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => removePreview(preview.id)}
                        >
                          <Trash2 size={14} />
                        </Button>

                        <div className="flex flex-col items-center">
                          {preview.type === "image" ? (
                            <div className="h-20 w-full overflow-hidden rounded mb-2">
                              <img
                                src={preview.url || "/placeholder.svg"}
                                alt={preview.name}
                                className="h-full w-full object-cover"
                              />
                            </div>
                          ) : (
                            <div className="h-20 w-full flex items-center justify-center">
                              {preview.type === "pdf" ? (
                                <FileText className="h-10 w-10 text-red-500" />
                              ) : preview.type === "doc" ? (
                                <FileText className="h-10 w-10 text-blue-500" />
                              ) : (
                                <File className="h-10 w-10 text-gray-500" />
                              )}
                            </div>
                          )}
                          <p className="text-xs truncate w-full text-center">{preview.name}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex justify-end space-x-2 pt-4">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setCreateDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={isSubmitting} 
                    className="bg-[#0263cc] hover:bg-[#0263cc]/90 text-white"
                  >
                    {isSubmitting ? (
                      <>
                        <Clock className="mr-2 h-4 w-4 animate-spin" /> Posting...
                      </>
                    ) : (
                      "Post Announcement"
                    )}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </CardHeader>

        <CardContent className="p-6">
          {error && (
            <Alert variant="destructive" className="mb-6 bg-red-50 text-red-800 border-red-200 rounded-lg">
              <AlertTriangle className="h-5 w-5 mr-2" />
              <AlertDescription className="font-medium">{error}</AlertDescription>
            </Alert>
          )}

          {/* Announcements Table */}
          <div className="space-y-4">
            <h4 className="text-lg font-medium flex items-center">
              <FileText className="h-5 w-5 mr-2 text-[#0263cc]" />
              Current Announcements
            </h4>

            {loading ? (
              <div className="flex justify-center items-center h-32">
                <Clock className="animate-spin h-5 w-5 text-[#0263cc]" />
                <span className="ml-2 text-gray-600">Loading announcements...</span>
              </div>
            ) : announcements.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No announcements available</div>
            ) : (
              <div className="rounded-md border overflow-hidden">
                <Table>
                  <TableHeader className="bg-gray-50">
                    <TableRow>
                      <TableHead className="font-semibold">Title</TableHead>
                      <TableHead className="font-semibold">Audience</TableHead>
                      <TableHead className="font-semibold">Date</TableHead>
                      <TableHead className="font-semibold">Attachments</TableHead>
                      <TableHead className="font-semibold text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {announcements.map((announcement) => (
                      <TableRow 
                        key={announcement._id}
                        className="cursor-pointer hover:bg-gray-50"
                        onClick={() => openDetailsModal(announcement)}
                      >
                        <TableCell className="font-medium">{announcement.title}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            announcement.role === "teacher" 
                              ? "bg-purple-100 text-purple-800" 
                              : announcement.role === "student"
                                ? "bg-green-100 text-green-800"
                                : "bg-blue-100 text-blue-800"
                          }`}>
                            {announcement.role === "all"
                              ? "Everyone"
                              : announcement.role === "teacher"
                                ? "Teachers Only"
                                : "Students Only"}
                          </span>
                        </TableCell>
                        <TableCell className="flex items-center">
                          <Calendar className="h-4 w-4 mr-2 text-gray-500" />
                          {formatDate(announcement.createdAt)}
                        </TableCell>
                        <TableCell>
                          {announcement.attachments && announcement.attachments.length > 0 ? (
                            <span className="flex items-center">
                              <File className="h-4 w-4 mr-1 text-gray-500" />
                              {announcement.attachments.length}
                            </span>
                          ) : (
                            <span className="text-gray-400">None</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right space-x-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              openDetailsModal(announcement);
                            }}
                            title="View Details"
                            className="text-[#0263cc] hover:text-[#0263cc]/80"
                          >
                            <Eye size={16} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            <Pagination
              currentPage={page}
              totalItems={total}
              itemsPerPage={limit}
              onPageChange={setPage}
              onItemsPerPageChange={(newLimit) => { setLimit(newLimit); setPage(1); }}
              itemLabel="announcements"
            />
          </div>
        </CardContent>
      </Card>

      {/* Announcement Details Modal */}
      <Dialog open={detailsModalOpen} onOpenChange={setDetailsModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedAnnouncement && (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl font-semibold">{selectedAnnouncement.title}</DialogTitle>
              </DialogHeader>
              
              <div className="mt-4 space-y-4">
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 mr-1" />
                    Posted on: {formatDate(selectedAnnouncement.createdAt)}
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    selectedAnnouncement.role === "teacher" 
                      ? "bg-purple-100 text-purple-800" 
                      : selectedAnnouncement.role === "student"
                        ? "bg-green-100 text-green-800"
                        : "bg-blue-100 text-blue-800"
                  }`}>
                    For: {selectedAnnouncement.role === "all"
                      ? "Everyone"
                      : selectedAnnouncement.role === "teacher"
                        ? "Teachers Only"
                        : "Students Only"}
                  </span>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="whitespace-pre-line">{selectedAnnouncement.content}</p>
                </div>

                {/* Display Attachments */}
                {selectedAnnouncement.attachments && selectedAnnouncement.attachments.length > 0 && (
                  <div className="mt-4">
                    <h6 className="text-sm font-medium mb-2">Attachments ({selectedAnnouncement.attachments.length})</h6>
                    <div className="grid grid-cols-2 gap-3">
                      {selectedAnnouncement.attachments.map((attachment, index) => (
                        <div 
                          key={index} 
                          className="flex items-center space-x-2 p-2 border rounded-md bg-white hover:bg-gray-50 transition"
                        >
                          {renderFileIcon(attachment.fileType)}
                          <span className="text-sm truncate flex-1">
                            {attachment.fileName || `Attachment ${index + 1}`}
                          </span>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => viewAttachment(attachment)}
                            title="View"
                            className="text-[#0263cc] hover:bg-[#0263cc]/10"
                          >
                            <Eye size={16} />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Full-screen Image Preview Modal */}
      <Dialog open={previewModalOpen} onOpenChange={setPreviewModalOpen}>
        <DialogContent className="max-w-5xl max-h-screen p-0">
          <div className="relative">
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-2 top-2 bg-white rounded-full text-black z-10"
              onClick={() => setPreviewModalOpen(false)}
            >
              <X size={20} />
            </Button>
            <img
              src={selectedImage || "/placeholder.svg"}
              alt="Preview"
              className="max-h-[80vh] object-contain mx-auto"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default AnnouncementsContent